// Timezone constants for Vietnam
const UTC_OFFSET = 7; // UTC+7 (Vietnam)
const UTC_OFFSET_MS = UTC_OFFSET * 60 * 60 * 1000; // Convert hours to milliseconds
const VIETNAM_TIMEZONE = 'Asia/Ho_Chi_Minh';

/**
 * Parse an incoming date string or Date object to a valid Date in Vietnam timezone
 * Handles ISO strings with/without Z, space-separated dates, and Date instances
 */
export const parseDateToVN = (dateInput: string | Date): Date => {
  if (!dateInput) return new Date(NaN);
  if (dateInput instanceof Date) return dateInput;
  if (typeof dateInput === 'string') {
    const trimmed = dateInput.trim();
    if (!trimmed) return new Date(NaN);
    // If date string has no timezone offset (e.g., "2026-09-12T15:26:01" or "2026-09-12 15:26:01"),
    // it was emitted by backend as local Vietnam time. Append +07:00 to prevent browser timezone shifting.
    if (!trimmed.endsWith('Z') && !/[+-]\d{2}(:\d{2})?$/.test(trimmed)) {
      const normalized = trimmed.replace(' ', 'T');
      return new Date(`${normalized}+07:00`);
    }
    return new Date(trimmed);
  }
  return new Date(dateInput);
};

/**
 * Convert UTC+0 server date to Vietnam UTC+7 local date
 * @param utcDate - Date string or Date object from server
 * @returns Date object in Vietnam UTC+7
 */
export const convertUTCToLocal = (utcDate: string | Date): Date => {
  return parseDateToVN(utcDate);
};

/**
 * Convert Vietnam UTC+7 local date to UTC+0 server date
 * @param localDate - Date string or Date object in Vietnam UTC+7
 * @returns Date object in UTC+0
 */
export const convertLocalToUTC = (localDate: string | Date): Date => {
  const date = parseDateToVN(localDate);
  return new Date(date.getTime() - UTC_OFFSET_MS);
};

/**
 * Format date to Vietnam UTC+7 local date string
 * @param utcDate - Date string from server
 * @param format - Output format ('date', 'datetime', 'time', 'relative', 'vietnamese')
 * @param locale - Locale for formatting ('en-US', 'vi-VN')
 * @returns Formatted date string in Vietnam UTC+7
 */
export const formatDateUTC7 = (
  utcDate: string | Date,
  format: 'date' | 'datetime' | 'time' | 'relative' | 'vietnamese' = 'date',
  locale: 'en-US' | 'vi-VN' = 'en-US'
): string => {
  if (!utcDate) return "";
  
  const localDate = parseDateToVN(utcDate);
  if (isNaN(localDate.getTime())) return "";
  
  switch (format) {
    case 'date':
      return localDate.toLocaleDateString(locale, {
        timeZone: VIETNAM_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    
    case 'datetime':
      return localDate.toLocaleString(locale, {
        timeZone: VIETNAM_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    
    case 'time':
      return localDate.toLocaleTimeString(locale, {
        timeZone: VIETNAM_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    
    case 'vietnamese':
      return formatVietnameseDate(localDate, locale);
    
    case 'relative':
      return formatRelativeTime(localDate, locale);
    
    default:
      return localDate.toISOString();
  }
};

/**
 * Format date in Vietnamese style
 * @param date - Date object
 * @param locale - Locale for formatting
 * @returns Vietnamese formatted date string
 */
const formatVietnameseDate = (date: Date, locale: 'en-US' | 'vi-VN'): string => {
  if (locale === 'vi-VN') {
    return date.toLocaleDateString('vi-VN', {
      timeZone: VIETNAM_TIMEZONE,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  } else {
    return date.toLocaleDateString('en-US', {
      timeZone: VIETNAM_TIMEZONE,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  }
};

/**
 * Format relative time (e.g., "2 hours ago", "yesterday")
 * @param date - Date object
 * @param locale - Locale for formatting
 * @returns Relative time string
 */
const formatRelativeTime = (date: Date, locale: 'en-US' | 'vi-VN' = 'en-US'): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (locale === 'vi-VN') {
    if (diffSeconds < 60) {
      return 'vừa xong';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} phút trước`;
    } else if (diffHours < 24) {
      return `${diffHours} giờ trước`;
    } else if (diffDays === 1) {
      return 'hôm qua';
    } else if (diffDays < 7) {
      return `${diffDays} ngày trước`;
    } else {
      return date.toLocaleDateString('vi-VN', {
        timeZone: VIETNAM_TIMEZONE,
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  } else {
    if (diffSeconds < 60) {
      return 'just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays === 1) {
      return 'yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        timeZone: VIETNAM_TIMEZONE,
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
};

/**
 * Format date for API requests (convert Vietnam UTC+7 to server UTC+0)
 * @param localDate - Date string in Vietnam UTC+7
 * @param isEndOfDay - Whether to set time to end of day (23:59:59)
 * @returns ISO string in UTC+0 for server
 */
export const formatDateForAPI = (localDate: string, isEndOfDay: boolean = false): string => {
  if (!localDate) return "";
  
  const time = isEndOfDay ? "23:59:59" : "00:00:00";
  const localDateTime = `${localDate}T${time}`;
  const utcDate = convertLocalToUTC(localDateTime);
  
  return utcDate.toISOString();
};

/**
 * Get current date in Vietnam UTC+7
 * @returns Current date string in YYYY-MM-DD format (Vietnam UTC+7)
 */
export const getCurrentDateUTC7 = (): string => {
  const now = new Date();
  const vietnamDate = new Date(now.getTime() + UTC_OFFSET_MS);
  return vietnamDate.toISOString().split('T')[0];
};

/**
 * Get current datetime in Vietnam UTC+7
 * @returns Current datetime string in ISO format (Vietnam UTC+7)
 */
export const getCurrentDateTimeUTC7 = (): string => {
  const now = new Date();
  const vietnamDate = new Date(now.getTime() + UTC_OFFSET_MS);
  return vietnamDate.toISOString();
};

/**
 * Get Vietnam timezone info
 * @returns Object with timezone information
 */
export const getVietnamTimezoneInfo = () => {
  const now = new Date();
  const vietnamDate = new Date(now.toLocaleString('en-US', { timeZone: VIETNAM_TIMEZONE }));
  
  return {
    timezone: VIETNAM_TIMEZONE,
    offset: UTC_OFFSET,
    currentTime: vietnamDate.toLocaleString('en-US', {
      timeZone: VIETNAM_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }),
    abbreviation: 'ICT' // Indochina Time
  };
};

// Legacy function for backward compatibility
export const formatToLocalDateTime = (dateStr: string, isEndOfDay: boolean = false): string => {
  if (!dateStr) return "";
  
  // Convert YYYY-MM-DD to LocalDateTime format (YYYY-MM-DDTHH:mm:ss)
  const time = isEndOfDay ? "23:59:59" : "00:00:00";
  return `${dateStr}T${time}`;
};